<template>
  <q-list>
    <div class="detail-header-pad">
      <q-item-wrapper>
        <template #before>
          <q-item-section avatar top>
            <profile-image
              :profile-image="client.profile_image"
              :initials="client.initials"
              size="48px"
            />
          </q-item-section>
        </template>
        <q-item-label
          class="text-h6 text-bold text-blue-8"
          style="word-break: break-word;"
          title="Display Name"
        >
          {{ client.display_name }}
        </q-item-label>

        <q-item-label
          v-if="client.contact_name"
          class="text-body2 q-pt-xs text-bold"
          style="word-break: break-word;"
          title="Contact Name"
        >
          {{ client.contact_name }}
        </q-item-label>
      </q-item-wrapper>

      <q-separator spaced inset />

      <!-- Actions / External Links -->
      <template v-if="withQuickLinks && !isArchived && (can(permissions.canLogReports) || can(permissions.canAddInvoices) || can(permissions.canAccessCalendar))">
        <q-tabs
          shrink
          outside-arrows
          mobile-arrows
          :breakpoint="0"
        >
          <stacked-btn
            v-if="can(permissions.canAccessCalendar)"
            bubble
            icon="mdi-calendar"
            label="Appointment"
            style="width: 100px;"
            class="full-height"
            @click="$emit('createAppointment')"
          />
          <stacked-btn
            v-if="can(permissions.canAddQuotations)"
            bubble
            icon="mdi-file-document"
            label="Quotation"
            style="width: 100px;"
            class="full-height"
            @click="$emit('createQuotation')"
          />
          <stacked-btn
            v-if="can(permissions.canLogReports)"
            bubble
            icon="mdi-file-chart"
            label="Report"
            style="width: 100px;"
            class="full-height"
            @click="$emit('createReport')"
          />
          <stacked-btn
            v-if="can(permissions.canAddInvoices)"
            bubble
            icon="mdi-cash"
            label="Invoice"
            style="width: 100px;"
            class="full-height"
            @click="$emit('createInvoice')"
          />
        </q-tabs>
        <q-separator
          v-if="$q.screen.gt.sm"
          spaced
          inset
        />
      </template>
    </div>

    <q-list dense class="detail-content">
      <!-- Financial summary -->
      <detail-item
        v-if="financials"
        icon="mdi-scale-balance"
        name="Account Balance"
        :name-spacing="nameSpacing"
      >
        <q-item-label
          class="text-h6"
          :class="financials.outstanding > 0 ? 'text-red-7' : 'text-green-7'"
        >
          {{ friendlyNumber(financials.outstanding, true) }}
        </q-item-label>
        <q-item-label caption>
          {{ financials.outstanding > 0 ? 'Outstanding across unpaid invoices' : 'No outstanding balance' }}
        </q-item-label>
      </detail-item>

      <detail-item
        v-if="financials"
        icon="mdi-cash-multiple"
        name="Lifetime Revenue"
        :name-spacing="nameSpacing"
      >
        <q-item-label class="text-h6 text-blue-8">
          {{ friendlyNumber(financials.lifetime_revenue, true) }}
        </q-item-label>
        <q-item-label caption>
          Total payments received from this client
        </q-item-label>
      </detail-item>

      <!-- Emails -->
      <detail-item
        v-if="client.emails.length > 0"
        icon="mdi-email"
        name="Email Addresses"
        sticky
        :sticky-top="stickyTopPos"
        :name-spacing="nameSpacing"
      >
        <div
          v-for="(email, key) in client.emails"
          :key="key"
          class="q-mb-md"
        >
          <q-item-label class="text-caption text-grey-7">
            {{ email.field_name }} Email
          </q-item-label>
          <q-item-label>
            <basic-link @click="goToEmail(email.email_address)">
              {{ email.email_address }}
            </basic-link>
          </q-item-label>
        </div>
      </detail-item>

      <!-- Phones -->
      <detail-item
        v-if="client.phones.length > 0"
        icon="mdi-phone"
        name="Phone Numbers"
        sticky
        :sticky-top="stickyTopPos"
        :name-spacing="nameSpacing"
      >
        <div
          v-for="(phone, key) in client.phones"
          :key="key"
          class="q-mb-md"
        >
          <q-item-label class="text-caption text-grey-7">
            {{ phone.field_name }} Phone
          </q-item-label>
          <q-item-label>
            <basic-link @click="goToPhone(phone.full_phone)">
              {{ phone.full_phone }}
            </basic-link>
          </q-item-label>
        </div>
      </detail-item>

      <!-- Addresses -->
      <detail-item
        v-if="client.addresses.length > 0"
        icon="mdi-map-marker"
        name="Addresses"
        sticky
        :sticky-top="stickyTopPos"
        :name-spacing="nameSpacing"
      >
        <div
          v-for="(address, key) in client.addresses"
          :key="key"
          class="q-mb-md"
        >
          <q-item-label class="text-caption text-grey-7">
            {{ address.field_name }} Address
          </q-item-label>
          <q-item-label>
            <basic-link @click="goToAddress(address.full_address)">
              {{ address.full_address }}
            </basic-link>
          </q-item-label>
          <q-item-label
            v-if="address.note"
            class="text-caption text-grey-7"
          >
            <span class="text-bold q-mr-xs">Note:</span><nl2br
              :text="address.note"
            />
          </q-item-label>
        </div>
      </detail-item>

      <q-list
        v-if="can(permissions.canAccessClients)"
        dense
      >
        <!-- Timeline -->
        <detail-item
          icon="mdi-clipboard-file-outline"
          name="Notes & Files"
          class="q-mt-xs notes-section"
          with-input
          sticky
          :sticky-top="stickyTopPos"
          :name-spacing="nameSpacing"
        >
          <simple-timeline
            :timeline="timeline"
            :read-only="!canEdit"
            :hide-no-entries-text="canEdit"
            disable-add-files
            simple-note
            @add-note="(note, done) => baseAddNote(note, done, client.id, timeline)"
            @update-note="(note, done) => baseUpdateNote(note, done)"
            @remove-note="(entryId, reference) => baseRemoveNote(entryId, reference)"
            @pin-note="(entryId, val, done, reference) => basePinNote(entryId, val, done, reference)"
            @save-attachments="(keepIds, removeObjs, done) => baseSaveAttachments(keepIds, removeObjs, done, client.id, timeline)"
            @remove-attachment="(entryId, key, reference) => baseRemoveAttachment(entryId, key, client.id, reference)"
            @pin-attachment="(entryId, val, key, done, reference) => basePinAttachment(entryId, val, key, done, client.id, reference)"
          >
            <template #buttons>
              <q-btn
                color="blue-7"
                size="12px"
                flat
                dense
                round
                icon="mdi-chart-gantt"
                @click="$emit('goToTimeline')"
              >
                <q-tooltip>View Timeline</q-tooltip>
              </q-btn>
            </template>
          </simple-timeline>
        </detail-item>
      </q-list>

      <detail-item
        v-if="client.timezone_details"
        icon="mdi-map-clock"
        name="Timezone"
        :value="client.timezone"
        :name-spacing="nameSpacing"
      >
        <q-item-label>
          {{ client.timezone_details.zone }}
        </q-item-label>
        <q-chip
          square
          dense
          size="12px"
          class="q-mx-none"
        >
          {{ client.timezone_details.abbr }} | {{ client.timezone_details.diff_from_gmt }}
        </q-chip>
      </detail-item>

      <!-- Website -->
      <detail-item
        v-if="client.website"
        icon="mdi-web"
        name="Website"
        :name-spacing="nameSpacing"
      >
        <basic-link @click="goToHref(client.website, true)">
          {{ client.website }}
        </basic-link>
      </detail-item>

      <!-- Groups -->
      <detail-item
        v-if="withGroups && client.groups.length > 0"
        icon="mdi-account-group"
        name="Groups"
        sticky
        :sticky-top="stickyTopPos"
        :with-input="31"
        :name-spacing="nameSpacing"
      >
        <AllGroups
          :groups="client.groups"
          @reload="$emit('reload') "
        />
      </detail-item>

      <!-- How did you hear about us? -->
      <detail-item
        v-if="client.heard_from_via"
        icon="mdi-account-voice"
        name="Heard About Us"
        :value="client.heard_from_via"
        :name-spacing="nameSpacing"
      />

      <!-- Communication method options -->
      <detail-item
        icon="mdi-bell-badge-outline"
        name="Preferred Communication Method"
        :name-spacing="nameSpacing"
      >
        <q-item-label>{{ defaultCommunicationTypeLabel }}</q-item-label>
        <q-item-label
          v-if="client.disable_reminders"
          caption
          class="text-bold text-red-7"
        >
          Default reminders disabled
        </q-item-label>
        <q-item-label
          v-if="client.prefer_contact_name"
          caption
          class="text-bold text-blue-7"
        >
          Contact name preferred on communications
        </q-item-label>
      </detail-item>

      <detail-item
        v-if="client.role_labels && client.role_labels.length > 0"
        icon="mdi-account-tie"
        name="Roles"
        :name-spacing="nameSpacing"
      >
        <div
          class="relative-position"
          style="top: -4px; left: -5px;"
        >
          <q-chip
            v-for="(label, key) in client.role_labels"
            :key="key"
            :label="label"
            dense
            square
            class="text-caption"
          />
        </div>
      </detail-item>

      <detail-item
        v-if="client.industry_labels && client.industry_labels.length > 0"
        icon="mdi-domain"
        name="Land Classification"
        :name-spacing="nameSpacing"
      >
        <div
          class="relative-position"
          style="top: -4px; left: -5px;"
        >
          <q-chip
            v-for="(label, key) in client.industry_labels"
            :key="key"
            :label="label"
            dense
            square
            class="text-caption"
          />
        </div>
      </detail-item>

      <!-- Invoice Due Date override -->
      <detail-item
        v-if="client.invoicing_default_due_date !== null"
        icon="mdi-cash"
        name="Default Invoice Due Date"
        :value="`${client.invoicing_default_due_date} Days`"
        :name-spacing="nameSpacing"
      />

      <!-- Strata Number -->
      <detail-item
        v-if="client.strata_identifier"
        icon="mdi-id-card"
        name="Strata Number"
        :value="client.strata_identifier"
        :name-spacing="nameSpacing"
      />

      <detail-item
        v-if="client.inventory_markup_percentage"
        icon="mdi-percent"
        name="Inventory Markup Percentage"
        :value="client.inventory_markup_percentage + '%'"
        :name-spacing="nameSpacing"
      />

      <ClientConnectDetailItem
        :client="client"
        :is-archived="isArchived"
        :name-spacing="nameSpacing"
      />

      <detail-item
        v-if="client.reference_id"
        icon="mdi-book-open-page-variant-outline"
        name="Reference Number"
        :value="client.reference_id"
        :name-spacing="nameSpacing"
      />

      <detail-item
        icon="mdi-content-duplicate"
        name="Created On"
        :name-spacing="nameSpacing"
      >
        <q-item-label>{{ $formatSystemDate(client.created_at, luxonFormats.FRIENDLY_DATE) }}</q-item-label>
        <q-item-label
          v-if="client.creator"
          caption
        >
          By {{ client.creator.name }}
        </q-item-label>
      </detail-item>

      <q-list v-if="client.provider_details">
        <sticky-header class="q-mt-lg" anchor="dialog-menubar">
          Accounting Provider
        </sticky-header>
        <accounting-provider
          :details="client.provider_details"
          :can-sync="!isArchived"
          class="q-mx-md"
          @sync="syncModel"
        />
      </q-list>
    </q-list>
  </q-list>
</template>

<script>
import optionsMixin from './../../mixins/optionsMixin'
import clientTimelineMethodsMixin from 'components/app/clients/partials/mixins/clientTimelineMethodsMixin'
import communicationMixin from 'components/partials/communications/communicationMixin'

import ClientConnectDetailItem from 'components/app/clients/partials/view/panels/clientConnect/DetailItem'
import AllGroups from './groups/AllGroups'
import { capitalize } from 'vue'

export default {
  components: {
    AllGroups, ClientConnectDetailItem
  },
  mixins: [optionsMixin, clientTimelineMethodsMixin, communicationMixin],
  props: {
    client: {
      required: true,
      type: Object
    },
    financials: {
      type: Object,
      default: null
    },
    timeline: {
      type: Array,
      default: () => {
        return []
      }
    },
    isArchived: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    withQuickLinks: {
      type: Boolean,
      default: false
    },
    withGroups: {
      type: Boolean,
      default: true
    }
  },
  emits: ['reload', 'createAppointment', 'createQuotation', 'createReport', 'createInvoice', 'goToTimeline', 'reload'],
  data () {
    return {
      ecardDialogOpen: false,
      stickyTopPos: 86,
      nameSpacing: 170
    }
  },
  computed: {
    defaultCommunicationTypeLabel () {
      return this.getFieldValue(this.client.default_communication_method, 'label')
    }
  },
  methods: {
    capitalize,
    async syncModel () {
      this.$loading.show()
      const { status, payload } = await this.$post('v1/clients/provider/sync', {
        id: this.client.id
      })
      this.$loading.hide()
      if (status === 'success') {
        if (payload.has_dependencies) {
          this.$q.dialog({
            title: 'Dependencies',
            message: 'This client has dependant items which will be synced first. If the dependant items sync successfully, this client will then automatically sync.'
          })
        }
        else {
          this.$q.notify({
            color: 'orange',
            textColor: 'black',
            message: 'Client will now sync'
          })
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
@media (max-width: 600px) {
  // The notes/files section needs breathing room at the bottom
  .detail-item.notes-section {
    padding-bottom: 20px;
  }
}
</style>
